import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, inject, Input, input, isDevMode, output, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { first } from 'rxjs';
import { Client, GePredixEquipmentDto } from '../../../../../../../api/api-clientInformation';
import { AddEquipmentsDialogComponent } from '../add-equipments-dialog/add-equipments-dialog.component';
import { PolicyService } from '../../../../../../services/roles/policy.service';
import { FilterResponse, TableColumnFilterComponent } from '@fdm/shared';
import { filterTable } from '@fdm/utils';

const DEFAULT_PAGES = [25];


@Component({
  selector: 'app-ge-predix-table',
  imports: [

    CommonModule,
    RouterModule,

    // Material
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    MatCheckboxModule,

    // Components
    TableColumnFilterComponent
  ],
  templateUrl: './ge-predix-table.component.html',
  styleUrl: './ge-predix-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GEPredixTableComponent implements AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;

  changed = output<GePredixEquipmentDto[]>();

  @Input()
  public set values(items: GePredixEquipmentDto[]) {
    this._values = items;
    this.dataSource.data = items;
    this.tempTableData = [...this.dataSource.data];
  }

  public get values() {
    return this._values;
  }

  private _values: GePredixEquipmentDto[] = [];

  private _dialog = inject(MatDialog);
  private _policy = inject(PolicyService);
  readonly apiClient = inject(Client);

  canEdit$ = this._policy.canEditTable$;

  displayedColumns: string[] = ['id', 'equipmentTag', 'descriptionEn', 'integrityConsequence', 'assetConsequence', 'gePredixEquipmentType', 'gePredixPlant', 'gePredixUnit'];

  dataSource = new MatTableDataSource<GePredixEquipmentDto>();

  allRecords: GePredixEquipmentDto[] = [];

  selection = new SelectionModel<GePredixEquipmentDto>(true, []);

  pageSizeOptions = DEFAULT_PAGES;

  get showDelete() {
    return this.selection.selected?.length > 0;
  }

  isDev = false;

  constructor() {
    this.isDev = isDevMode();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    // this.dataSource.data = this.values();
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear()
    } else {
      this.dataSource.data.forEach(row => {
        this.selection.select(row);
      });
    }
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected > 0 && numSelected === numRows;
  }

  checkRow(row: GePredixEquipmentDto) {
    this.selection.toggle(row)
  }

  addEquipment(): void {
    // const currentFacility = this.dataSource.data?.[0]?.facility;

    this._dialog
      .open<AddEquipmentsDialogComponent, any, GePredixEquipmentDto[]>(AddEquipmentsDialogComponent, {
        width: '400px',
        height: '100vh',
        position: { right: '0' },
        data: {
          selectedItems: this.dataSource.data ?? [],
          source: 'Predix',
          // reportFacility: currentFacility,
        },
      })
      .afterClosed()
      .pipe(first())
      .subscribe((data) => {
        if (data) {
          const values = [...this.dataSource.data];
          const sortedValues = data.filter((item) => {
            return values.length ? !values.some((val) => val.id === item.id) : true;
          });

          this.dataSource.data = [...values, ...sortedValues];

          this.changed.emit(this.dataSource.data);
        }
      });
  }

  refresh() {
    this.dataSource.data = [...this.values];
    this.tempTableData = [...this.dataSource.data];
  }

  deleteEquipment() {
    const selectedItems = this.selection.selected as GePredixEquipmentDto[];
    const filteredItems = this.dataSource.data.filter(item => !selectedItems.some(selected => selected.id === item.id))

    this.dataSource.data = filteredItems;
    this.tempTableData = [...this.dataSource.data];
    this.changed.emit(filteredItems);
    this.selection.clear();
  }

  export() {}

  filters: Record<string, FilterResponse | null> = {};
  tempTableData: any[] = [];

  applyFilter(res: FilterResponse | null, column: string) {
    this.filters[column] = res;
    this.dataSource.data = filterTable(this.tempTableData, column, res);
  }

  isActive(column: string) {    
    return this.filters[column] != null;
  }
}
